import {UserDto} from '@brage/dto';
import {AxiosError} from 'axios';
import {createContext, useContext, useEffect, useRef, useState} from 'react';
import {api} from '../shared/data-fetching/api-client';
import {capitalize} from '../shared/utils';

export interface CurrentUserState {
  user: UserDto | null;
  isLoading: boolean;
  apiStatus: number | undefined;
}

const CurrentUserContext = createContext<CurrentUserState | null>(null);

function CurrentUserProvider({children}: {children: React.ReactNode}) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState<number>();

  const isInsurance = window.location.href.includes('payment-insurance');
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    setIsLoading(true);

    api
      .getCurrentUser(isInsurance)
      .then((user) => {
        if (user) {
          user.firstName = capitalize(user.firstName.trim());
          user.lastName = capitalize(user.lastName.trim());

          user.companies = user.companies.filter((company) => company.orgNo !== user.ssn);

          user.companies.forEach((company) => {
            company.name = company.name.trim().toUpperCase();
          });

          user.companies.sort((a, b) => a.name.localeCompare(b.name));
        }
        setUser(user);
        setApiStatus(200);
      })
      .catch((err: AxiosError) => {
        setUser(null);
        setApiStatus(err.response?.status || 500);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CurrentUserContext.Provider value={{user, isLoading, apiStatus}}>
      {children}
    </CurrentUserContext.Provider>
  );
}

function useCurrentUser(): CurrentUserState {
  const context = useContext(CurrentUserContext);
  if (!context) throw new Error('useCurrentUser must be used within CurrentUserProvider');
  return context;
}

function useUser(): UserDto {
  const {user} = useCurrentUser();
  if (!user)
    throw new Error(
      'useUser must be used within CurrentUserProvider and user must be authenticated',
    );
  return user;
}

export {CurrentUserProvider, useCurrentUser, useUser};
